/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';
import { canManageCompanyCredentials as canManageCompanyCredentialsByPermission, getCredentialConnectionExecutionPriority } from '@shared/utils';
import overlayCompanyConnectionManagement from '@/redux/selectors/pieveSelectors/lib/overlayCompanyConnectionManagement.js';
import { authAdmin, authPermissions, companyId } from '@/redux/selectors/userSelectors.js';

const rawConnections = (state) => state?.pieve?.connections || EMPTY_ARRAY;
const rawCompanyConnections = (state) => state?.pieve?.companyConnections || EMPTY_ARRAY;

export const connections = rawConnections;

export const connectionsWithCompanyManagement = createSelector([rawConnections, rawCompanyConnections], (items, companyItems) => {
  return overlayCompanyConnectionManagement({ connections: items, companyConnections: companyItems });
});

export const personalConnections = createSelector([rawConnections], (items) => {
  return items.filter((c) => c?.isCompany === false);
});

export const companyConnections = rawCompanyConnections;

/* Whether the signed-in person has a working Google Sheets connection they may use — the same list
   and rule the server applies when a Workbook report is opened in Google Sheets. */
export const hasGoogleSheetsConnection = createSelector([rawConnections], (items) => items.some((connection) => connection?.appSlug === 'googleSheets' && connection?.valid !== false));

export const mainCompanyConnections = createSelector([rawCompanyConnections], (items) => {
  return items.filter((connection) => connection?.canManage === true || getCredentialConnectionExecutionPriority(connection?.status) > 0);
});

export const currentCredential = (state) => state?.pieve?.currentCredential || null;

// Company selection is per tab (`sessionCompanyId`) while `authCompanyId` mirrors the
// Clerk default company shared by every tab and rewritten on each switch. The API
// authorizes every request for the tab's effective company (`x-company-id`), so these
// predicates must not require the two to match: they need a resolved company plus the
// shared rule (2026-09-21 regression: edit controls vanished in every tab but the
// last-switched one).
export const canManageCompanyCredentials = createSelector([authAdmin, authPermissions, companyId], (admin, permissions, activeScope) => {
  return Boolean(activeScope) && canManageCompanyCredentialsByPermission({ permissions, isAdmin: admin });
});

export const canDeleteCompanyCredentials = createSelector([authAdmin, authPermissions, companyId], (admin, permissions, activeScope) => {
  return Boolean(activeScope) && (admin || permissions.includes('credential:delete'));
});

export const credentialImpactResponse = (state, loadRequestId) => {
  const response = state?.ui?.credentialImpactResponse;
  return response?.meta?.loadRequestId === loadRequestId ? response : null;
};

export const appDefinitionForId = (state, appId) => {
  return (state?.pieve?.appDefinitions || EMPTY_ARRAY).find((app) => app?.id === appId) || null;
};
